/**
 * Event Team Management System — API client.
 *
 * Covers every non-bot endpoint documented in `docs/EVENT_TEAM_FRONTEND_GUIDE.md`
 * / `docs/EVENT_TEAM_API_DOCS.md` (195 user endpoints; the WS upgrade lives in
 * `hooks/useEventRealtime.ts`).
 *
 * Path notes (guide §7.3):
 * - Almost everything lives under `{base}/events/...`.
 * - `GET /events-with-kanban-summary` is a TOP-LEVEL path (Hono route-conflict avoidance).
 * - The kanban cross-endpoints live under `{base}/kanban/...`.
 *
 * Auth: Cloudflare Access session cookies (the browser sends `Cf-Access-Jwt-Assertion`
 * automatically behind Access) — same convention as the other Argus services.
 */

import type { CalendarEvent } from '../types/calendar';
import type { CheckRewardPreview, EventRewardPrefix } from '../types/eventTeam';
import type {
  AdoptCardAsEventRequest,
  ApplyWorkflowResponse,
  AwardPointsRequest,
  CreateCommandCheckRequest,
  CreateEventEntityLinkRequest,
  CreateEventItemRequest,
  CreateEventKanbanLinkRequest,
  CreateEventMemberRequest,
  CreateEventModpackLinkRequest,
  CreateEventParticipantRequest,
  CreateEventProjectRequest,
  CreateEventServerLinkRequest,
  CreateEventTagRequest,
  CreateEventTaskRequest,
  CreateEventWebLinkRequest,
  CreateEventWorkflowRequest,
  CreateEventWorkflowStageRequest,
  CreatePlayerTeamRequest,
  EsQuerySpec,
  EventActivityLog,
  EventAssignment,
  EventAssignmentRole,
  EventAssignmentStatus,
  EventCalendarReverseLink,
  EventCheckResult,
  EventCheckSummary,
  EventCheckViolation,
  EventComment,
  EventCommandCheck,
  EventEntityLinksResponse,
  EventItem,
  EventItemFull,
  EventItemListParams,
  EventItemStatus,
  EventKanbanLink,
  EventMedia,
  EventMemberPointsResponse,
  EventMemberPrefixesResponse,
  EventModpackLink,
  EventPaginatedResponse,
  EventParticipant,
  EventPastItem,
  EventPlayerTeam,
  EventPlayerTeamMember,
  EventPlayerTeamWithMembers,
  EventPointsEntry,
  EventPointsLedgerParams,
  EventPointsMemberSummary,
  EventPointsStats,
  EventPointsTotal,
  EventPrefixEligibilityRow,
  EventPrefixGrant,
  EventPrefixRequirement,
  EventProject,
  EventProjectFull,
  EventProjectListParams,
  EventProjectStatus,
  EventRetrospective,
  EventServerLink,
  EventTag,
  EventTask,
  EventTaskStatus,
  EventTeamMember,
  EventTeamStats,
  EventTicketLink,
  EventTimelineEntry,
  EventUpcomingTask,
  EventWatcher,
  EventWebLink,
  EventWithKanbanSummary,
  EventWorkflow,
  EventWorkflowStage,
  EventWorkflowWithStages,
  EventWorkspaceSummary,
  GrantPrefixRequest,
  GrantPrefixResponse,
  PlayerPrefixesResponse,
  Prefix,
  PrefixHolding,
  PrefixWithRequirement,
  PromoteEventToCardRequest,
  ScheduleEventRequest,
  ScheduleEventResponse,
  SubmitCheckResultsResponse,
  SubmitCheckTeamResult,
  UpdateCommandCheckRequest,
  UpdateEventItemRequest,
  UpdateEventKanbanLinkRequest,
  UpdateEventMemberRequest,
  UpdateEventModpackLinkRequest,
  UpdateEventProjectRequest,
  UpdateEventServerLinkRequest,
  UpdateEventTagRequest,
  UpdateEventTaskRequest,
  UpdateEventWebLinkRequest,
  UpdateEventWorkflowRequest,
  UpdateEventWorkflowStageRequest,
  UpdatePlayerTeamRequest,
  UpsertEventRetrospectiveRequest,
  UpsertPrefixRequirementRequest,
} from '../types/eventTeam';
import type { KanbanCard } from '../types/kanban';
import type {
  EventQuestBundle,
  EventQuestChapterGroupsResponse,
  EventQuestCount,
  EventQuestEditorDocument,
  EventQuestFilePublic,
  EventQuestJsonUpsertRequest,
  EventQuestManifest,
  EventQuestUpsertResult,
} from '../types/eventQuests';

export const EVENT_TEAM_API_BASE_URL = 'https://spy.playcdu.co/api/notes';

/** Error thrown for non-2xx responses; carries the status for 404/409/422-specific UX. */
export class EventTeamApiError extends Error {
  status: number;
  body: string;

  constructor(status: number, statusText: string, body: string) {
    let message = `API Error: ${status} ${statusText}`;
    try {
      const parsed = JSON.parse(body) as { error?: string };
      if (parsed && typeof parsed.error === 'string') message = parsed.error;
      else message += ` - ${body}`;
    } catch {
      if (body) message += ` - ${body}`;
    }
    super(message);
    this.name = 'EventTeamApiError';
    this.status = status;
    this.body = body;
  }
}

export interface PromoteEventToCardResponse {
  card: KanbanCard;
  link: EventKanbanLink;
  /** `false` (HTTP 200) when the event already had a card on that board — treat as success. */
  created_card: boolean;
}

export interface AdoptCardAsEventResponse {
  event: EventItem;
  link: EventKanbanLink;
  created_event: boolean;
}

type QueryParams = Record<string, string | number | boolean | undefined | null>;

class EventTeamApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${EVENT_TEAM_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies.
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new EventTeamApiError(response.status, response.statusText, errorText);
    }
    return response.json();
  }

  private async requestRaw(endpoint: string, options?: RequestInit): Promise<Response> {
    const response = await fetch(`${EVENT_TEAM_API_BASE_URL}${endpoint}`, { ...options });
    if (!response.ok) {
      const errorText = await response.text();
      throw new EventTeamApiError(response.status, response.statusText, errorText);
    }
    return response;
  }

  private json<T>(endpoint: string, method: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method,
      ...(body !== undefined
        ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        : {}),
    });
  }

  private buildQueryString(params: QueryParams): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  /** Normalize `bare array` vs `{ results, total }` responses into the envelope. */
  private normalizePaginated<T>(raw: unknown, page: number, limit: number): EventPaginatedResponse<T> {
    if (Array.isArray(raw)) {
      return { results: raw as T[], total: raw.length, page, limit };
    }
    if (raw && typeof raw === 'object') {
      const o = raw as Partial<EventPaginatedResponse<T>> & { results?: unknown };
      const results = Array.isArray(o.results) ? (o.results as T[]) : [];
      return {
        results,
        total: typeof o.total === 'number' ? o.total : results.length,
        page: typeof o.page === 'number' ? o.page : page,
        limit: typeof o.limit === 'number' ? o.limit : limit,
        total_pages: typeof o.total_pages === 'number' ? o.total_pages : undefined,
      };
    }
    return { results: [], total: 0, page, limit };
  }

  // ════════════════════════════════════════════════════════════════════════
  // Event Team Members (13)
  // ════════════════════════════════════════════════════════════════════════

  async listTeamMembers(params?: {
    role?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<EventPaginatedResponse<EventTeamMember>> {
    const raw = await this.request<unknown>(`/events/team${this.buildQueryString(params || {})}`);
    return this.normalizePaginated<EventTeamMember>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  async createTeamMember(data: CreateEventMemberRequest): Promise<EventTeamMember> {
    return this.json('/events/team', 'POST', data);
  }

  async countTeamMembers(status?: string): Promise<{ count: number }> {
    return this.request(`/events/team/count${this.buildQueryString({ status })}`);
  }

  async searchTeamMembers(q: string): Promise<EventTeamMember[]> {
    return this.request(`/events/team/search${this.buildQueryString({ q })}`);
  }

  async getTeamMemberByUuid(uuid: string): Promise<EventTeamMember> {
    return this.request(`/events/team/uuid/${encodeURIComponent(uuid)}`);
  }

  async getTeamMember(id: string): Promise<EventTeamMember> {
    return this.request(`/events/team/${encodeURIComponent(id)}`);
  }

  async updateTeamMember(id: string, data: UpdateEventMemberRequest): Promise<EventTeamMember> {
    return this.json(`/events/team/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  async deleteTeamMember(id: string): Promise<{ ok: boolean; deleted?: string }> {
    return this.request(`/events/team/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async getTeamMemberActivity(id: string, page?: number, limit?: number): Promise<EventActivityLog[]> {
    return this.request(
      `/events/team/${encodeURIComponent(id)}/activity${this.buildQueryString({ page, limit })}`,
    );
  }

  async getTeamMemberAssignments(id: string, status?: EventAssignmentStatus | ''): Promise<EventAssignment[]> {
    return this.request(
      `/events/team/${encodeURIComponent(id)}/assignments${this.buildQueryString({ status })}`,
    );
  }

  async getTeamMemberPoints(id: string): Promise<EventMemberPointsResponse> {
    return this.request(`/events/team/${encodeURIComponent(id)}/points`);
  }

  async getTeamMemberPrefixes(id: string): Promise<EventMemberPrefixesResponse> {
    return this.request(`/events/team/${encodeURIComponent(id)}/prefixes`);
  }

  async getTeamMemberTasks(id: string, status?: EventTaskStatus | ''): Promise<EventTask[]> {
    return this.request(
      `/events/team/${encodeURIComponent(id)}/tasks${this.buildQueryString({ status })}`,
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Event Projects (11)
  // ════════════════════════════════════════════════════════════════════════

  async listProjects(params?: EventProjectListParams): Promise<EventPaginatedResponse<EventProject>> {
    const raw = await this.request<unknown>(
      `/events/projects${this.buildQueryString((params as QueryParams) || {})}`,
    );
    return this.normalizePaginated<EventProject>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  async createProject(data: CreateEventProjectRequest): Promise<EventProject> {
    return this.json('/events/projects', 'POST', data);
  }

  async countProjects(status?: string): Promise<{ count: number }> {
    return this.request(`/events/projects/count${this.buildQueryString({ status })}`);
  }

  async searchProjects(q: string, page?: number, limit?: number): Promise<EventProject[]> {
    return this.request(`/events/projects/search${this.buildQueryString({ q, page, limit })}`);
  }

  /** Event team dashboard stats (KV-cached). */
  async getStats(): Promise<EventTeamStats> {
    return this.request('/events/projects/stats');
  }

  async getProject(id: string): Promise<EventProjectFull> {
    return this.request(`/events/projects/${encodeURIComponent(id)}`);
  }

  async updateProject(id: string, data: UpdateEventProjectRequest): Promise<EventProject> {
    return this.json(`/events/projects/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  async deleteProject(id: string): Promise<{ ok: boolean; deleted?: string }> {
    return this.request(`/events/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async changeProjectStatus(id: string, status: EventProjectStatus, reason?: string): Promise<EventProject> {
    return this.json(`/events/projects/${encodeURIComponent(id)}/status`, 'POST', { status, reason });
  }

  async getProjectActivity(id: string, page?: number, limit?: number): Promise<EventActivityLog[]> {
    return this.request(
      `/events/projects/${encodeURIComponent(id)}/activity${this.buildQueryString({ page, limit })}`,
    );
  }

  async getProjectEvents(id: string, page?: number, limit?: number): Promise<EventItem[]> {
    const raw = await this.request<unknown>(
      `/events/projects/${encodeURIComponent(id)}/events${this.buildQueryString({ page, limit })}`,
    );
    return this.normalizePaginated<EventItem>(raw, page ?? 1, limit ?? 50).results;
  }

  // ════════════════════════════════════════════════════════════════════════
  // Events (13)
  // ════════════════════════════════════════════════════════════════════════

  async listEvents(params?: EventItemListParams): Promise<EventPaginatedResponse<EventItem>> {
    const raw = await this.request<unknown>(
      `/events/items${this.buildQueryString((params as QueryParams) || {})}`,
    );
    return this.normalizePaginated<EventItem>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  async createEvent(data: CreateEventItemRequest): Promise<EventItem> {
    return this.json('/events/items', 'POST', data);
  }

  async countEvents(status?: string, projectId?: string): Promise<{ count: number }> {
    return this.request(
      `/events/items/count${this.buildQueryString({ status, project_id: projectId })}`,
    );
  }

  /** Idea board — status=idea, priority-ordered (guide §1.2). */
  async listIdeas(page?: number, limit?: number): Promise<EventPaginatedResponse<EventItem>> {
    const raw = await this.request<unknown>(
      `/events/items/ideas${this.buildQueryString({ page, limit })}`,
    );
    return this.normalizePaginated<EventItem>(raw, page ?? 1, limit ?? 50);
  }

  /** Past events archive — completed/cancelled with retrospectives inline (guide §1.10). */
  async listPastEvents(page?: number, limit?: number): Promise<EventPaginatedResponse<EventPastItem>> {
    const raw = await this.request<unknown>(
      `/events/items/past${this.buildQueryString({ page, limit })}`,
    );
    return this.normalizePaginated<EventPastItem>(raw, page ?? 1, limit ?? 50);
  }

  async searchEvents(q: string, page?: number, limit?: number): Promise<EventItem[]> {
    const raw = await this.request<unknown>(
      `/events/items/search${this.buildQueryString({ q, page, limit })}`,
    );
    return this.normalizePaginated<EventItem>(raw, page ?? 1, limit ?? 50).results;
  }

  async listUpcomingEvents(withinDays?: number, limit?: number): Promise<EventItem[]> {
    return this.request(
      `/events/items/upcoming${this.buildQueryString({ within_days: withinDays, limit })}`,
    );
  }

  async getEvent(id: string): Promise<EventItemFull> {
    return this.request(`/events/items/${encodeURIComponent(id)}`);
  }

  async updateEvent(id: string, data: UpdateEventItemRequest): Promise<EventItem> {
    return this.json(`/events/items/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  async deleteEvent(id: string): Promise<{ ok: boolean; deleted?: string }> {
    return this.request(`/events/items/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async changeEventStatus(id: string, status: EventItemStatus, reason?: string): Promise<EventItem> {
    return this.json(`/events/items/${encodeURIComponent(id)}/status`, 'POST', { status, reason });
  }

  async moveEventToProject(id: string, projectId: string | null): Promise<EventItem> {
    return this.json(`/events/items/${encodeURIComponent(id)}/move-project`, 'POST', {
      project_id: projectId,
    });
  }

  async getEventActivity(id: string, page?: number, limit?: number): Promise<EventActivityLog[]> {
    return this.request(
      `/events/items/${encodeURIComponent(id)}/activity${this.buildQueryString({ page, limit })}`,
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Assignments (9)
  // ════════════════════════════════════════════════════════════════════════

  async getEventAssignments(eventId: string): Promise<EventAssignment[]> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/assignments`);
  }

  /** Lead-assign a roster member. */
  async assignToEvent(
    eventId: string,
    data: { member_id: string; role?: EventAssignmentRole },
  ): Promise<EventAssignment> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/assignments`, 'POST', data);
  }

  /**
   * Self-assign (guide §1.2). 404 means the caller is not on the roster —
   * show "ask an admin to add you to the event team".
   */
  async selfAssign(eventId: string, role?: EventAssignmentRole): Promise<EventAssignment> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/assignments/self`, 'POST', {
      role,
    });
  }

  async selfUnassign(eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/assignments/self`, {
      method: 'DELETE',
    });
  }

  async getProjectAssignments(projectId: string): Promise<EventAssignment[]> {
    return this.request(`/events/projects/${encodeURIComponent(projectId)}/assignments`);
  }

  async assignToProject(
    projectId: string,
    data: { member_id: string; role?: EventAssignmentRole },
  ): Promise<EventAssignment> {
    return this.json(`/events/projects/${encodeURIComponent(projectId)}/assignments`, 'POST', data);
  }

  async updateAssignment(
    id: string,
    data: { role?: EventAssignmentRole; status?: EventAssignmentStatus },
  ): Promise<EventAssignment> {
    return this.json(`/events/assignments/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  async completeAssignment(id: string): Promise<EventAssignment> {
    return this.request(`/events/assignments/${encodeURIComponent(id)}/complete`, { method: 'POST' });
  }

  /** Leads can remove anyone's assignment; members can remove their own. */
  async removeAssignment(id: string): Promise<{ ok: boolean }> {
    return this.request(`/events/assignments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Participants (4)
  // ════════════════════════════════════════════════════════════════════════

  async getEventParticipants(eventId: string): Promise<EventParticipant[]> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/participants`);
  }

  /** Upserts on repeat (same event + uuid). */
  async addParticipant(eventId: string, data: CreateEventParticipantRequest): Promise<EventParticipant> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/participants`, 'POST', data);
  }

  async removeParticipant(eventId: string, uuid: string): Promise<{ ok: boolean }> {
    return this.request(
      `/events/items/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(uuid)}`,
      { method: 'DELETE' },
    );
  }

  /** All events a player took part in. */
  async getParticipantHistory(uuid: string): Promise<(EventParticipant & { event?: EventItem | null; event_title?: string | null })[]> {
    return this.request(`/events/participants/${encodeURIComponent(uuid)}/history`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Watchers (3)
  // ════════════════════════════════════════════════════════════════════════

  /** Watchers get hourly email digests on changes. */
  async watchEvent(eventId: string): Promise<EventWatcher> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/watchers`, { method: 'POST' });
  }

  async unwatchEvent(eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/watchers`, {
      method: 'DELETE',
    });
  }

  async listEventWatchers(eventId: string): Promise<EventWatcher[]> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/watchers`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Points (6)
  // ════════════════════════════════════════════════════════════════════════

  /** Event manager only (`event_points`); server enforces 403. Delta signed, ≠ 0. */
  async awardPoints(data: AwardPointsRequest): Promise<EventPointsEntry> {
    return this.json('/events/points/award', 'POST', data);
  }

  /** KV-cached when no limit param. */
  async getPointsLeaderboard(limit?: number): Promise<EventPointsTotal[]> {
    return this.request(`/events/points/leaderboard${this.buildQueryString({ limit })}`);
  }

  async getPointsLedger(params?: EventPointsLedgerParams): Promise<EventPaginatedResponse<EventPointsEntry>> {
    const raw = await this.request<unknown>(
      `/events/points/ledger${this.buildQueryString((params as QueryParams) || {})}`,
    );
    return this.normalizePaginated<EventPointsEntry>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  async getMemberPointsSummary(uuid: string, page?: number, limit?: number): Promise<EventPointsMemberSummary> {
    return this.request(
      `/events/points/member/${encodeURIComponent(uuid)}${this.buildQueryString({ page, limit })}`,
    );
  }

  async getPointsStats(): Promise<EventPointsStats> {
    return this.request('/events/points/stats');
  }

  /** Correction — event manager only. */
  async deletePointsEntry(entryId: string): Promise<{ ok: boolean; deleted?: string }> {
    return this.request(`/events/points/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Prefixes (13)
  // ════════════════════════════════════════════════════════════════════════

  /** Live prefix catalog — READ-ONLY (guide §7.4); sort displays by `priority` asc. */
  async listPrefixes(params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<EventPaginatedResponse<Prefix>> {
    const raw = await this.request<unknown>(
      `/events/prefixes${this.buildQueryString(params || {})}`,
    );
    return this.normalizePaginated<Prefix>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  /** Every prefix, straight from the database (the bare list is a cache that can trail new prefixes by ~15 min). */
  async listAllPrefixes(): Promise<Prefix[]> {
    const pageSize = 200;
    const all: Prefix[] = [];
    for (let page = 1; ; page++) {
      const res = await this.listPrefixes({ page, limit: pageSize });
      all.push(...res.results);
      if (res.results.length < pageSize || all.length >= res.total) return all;
    }
  }

  async listPrefixCategories(): Promise<string[]> {
    return this.request('/events/prefixes/categories');
  }

  /** Members whose event points meet thresholds, with `already_holds` from live data. */
  async getPrefixEligibility(prefixId?: number): Promise<EventPrefixEligibilityRow[]> {
    return this.request(
      `/events/prefixes/eligibility${this.buildQueryString({ prefix_id: prefixId })}`,
    );
  }

  /** Prefix + its requirement overlay. */
  async getPrefix(id: number): Promise<PrefixWithRequirement> {
    return this.request(`/events/prefixes/${id}`);
  }

  /**
   * Apply a prefix (writes the LIVE userPrefix row) — event manager only.
   * 201 with `already_held: true` is success; 422 = player has no `user` record.
   */
  async grantPrefix(prefixId: number, data: GrantPrefixRequest): Promise<GrantPrefixResponse> {
    return this.json(`/events/prefixes/${prefixId}/grant`, 'POST', data);
  }

  /** Live holders of a prefix (from userPrefix). */
  async getPrefixHolders(prefixId: number, page?: number, limit?: number): Promise<EventPaginatedResponse<PrefixHolding>> {
    const raw = await this.request<unknown>(
      `/events/prefixes/${prefixId}/holders${this.buildQueryString({ page, limit })}`,
    );
    return this.normalizePaginated<PrefixHolding>(raw, page ?? 1, limit ?? 50);
  }

  /** Event grant history for a prefix. */
  async getPrefixGrants(prefixId: number): Promise<EventPrefixGrant[]> {
    return this.request(`/events/prefixes/${prefixId}/grants`);
  }

  async listAllPrefixGrants(params?: {
    status?: 'active' | 'revoked' | '';
    page?: number;
    limit?: number;
  }): Promise<EventPaginatedResponse<EventPrefixGrant>> {
    const raw = await this.request<unknown>(
      `/events/prefix-grants${this.buildQueryString(params || {})}`,
    );
    return this.normalizePaginated<EventPrefixGrant>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  /** Revoke — deletes the live userPrefix row (in-game immediately); 409 when already revoked. */
  async revokePrefixGrant(grantId: string): Promise<EventPrefixGrant | { ok: boolean }> {
    return this.request(`/events/prefix-grants/${encodeURIComponent(grantId)}/revoke`, {
      method: 'POST',
    });
  }

  async listPrefixRequirements(): Promise<EventPrefixRequirement[]> {
    return this.request('/events/prefix-requirements');
  }

  /** Admin: overlay table only — never writes to the live `prefix` table. */
  async upsertPrefixRequirement(prefixId: number, data: UpsertPrefixRequirementRequest): Promise<EventPrefixRequirement> {
    return this.json(`/events/prefixes/${prefixId}/requirements`, 'PUT', data);
  }

  async deletePrefixRequirement(prefixId: number): Promise<{ ok: boolean }> {
    return this.request(`/events/prefixes/${prefixId}/requirements`, { method: 'DELETE' });
  }

  /** "Check people" — works for ANY player, not just roster members. */
  async getPlayerPrefixes(uuid: string): Promise<PlayerPrefixesResponse> {
    return this.request(`/events/players/${encodeURIComponent(uuid)}/prefixes`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Workflows (9)
  // ════════════════════════════════════════════════════════════════════════

  async listWorkflows(activeOnly?: boolean): Promise<EventWorkflow[]> {
    return this.request(
      `/events/workflows${this.buildQueryString({ active_only: activeOnly ? 'true' : undefined })}`,
    );
  }

  async createWorkflow(data: CreateEventWorkflowRequest): Promise<EventWorkflowWithStages> {
    return this.json('/events/workflows', 'POST', data);
  }

  async getWorkflow(id: string): Promise<EventWorkflowWithStages> {
    return this.request(`/events/workflows/${encodeURIComponent(id)}`);
  }

  async updateWorkflow(id: string, data: UpdateEventWorkflowRequest): Promise<EventWorkflow> {
    return this.json(`/events/workflows/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  /** Admin; already-instantiated tasks survive. */
  async deleteWorkflow(id: string): Promise<{ ok: boolean }> {
    return this.request(`/events/workflows/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async addWorkflowStage(workflowId: string, data: CreateEventWorkflowStageRequest): Promise<EventWorkflowStage> {
    return this.json(`/events/workflows/${encodeURIComponent(workflowId)}/stages`, 'POST', data);
  }

  /** Fractional `position` supported for drag-reorder. */
  async updateWorkflowStage(stageId: string, data: UpdateEventWorkflowStageRequest): Promise<EventWorkflowStage> {
    return this.json(`/events/workflows/stages/${encodeURIComponent(stageId)}`, 'PATCH', data);
  }

  async deleteWorkflowStage(stageId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/workflows/stages/${encodeURIComponent(stageId)}`, {
      method: 'DELETE',
    });
  }

  /** Task due dates auto-computed from `scheduled_start + offset_days`. */
  async applyWorkflow(eventId: string, workflowId: string, assigneeUuid?: string): Promise<ApplyWorkflowResponse> {
    return this.json(
      `/events/items/${encodeURIComponent(eventId)}/apply-workflow/${encodeURIComponent(workflowId)}`,
      'POST',
      { assignee_uuid: assigneeUuid },
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Tasks (7)
  // ════════════════════════════════════════════════════════════════════════

  async getEventTasks(eventId: string, status?: EventTaskStatus | ''): Promise<EventTask[]> {
    return this.request(
      `/events/items/${encodeURIComponent(eventId)}/tasks${this.buildQueryString({ status })}`,
    );
  }

  async createTask(eventId: string, data: CreateEventTaskRequest): Promise<EventTask> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/tasks`, 'POST', data);
  }

  /** "What's coming" — each task includes `event_title`. */
  async listUpcomingTasks(withinDays?: number): Promise<EventUpcomingTask[]> {
    return this.request(
      `/events/tasks/upcoming${this.buildQueryString({ within_days: withinDays })}`,
    );
  }

  async getTasksByAssignee(uuid: string): Promise<EventUpcomingTask[]> {
    return this.request(`/events/tasks/assignee/${encodeURIComponent(uuid)}`);
  }

  async updateTask(taskId: string, data: UpdateEventTaskRequest): Promise<EventTask> {
    return this.json(`/events/tasks/${encodeURIComponent(taskId)}`, 'PATCH', data);
  }

  async completeTask(taskId: string): Promise<EventTask> {
    return this.request(`/events/tasks/${encodeURIComponent(taskId)}/complete`, { method: 'POST' });
  }

  async deleteTask(taskId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/tasks/${encodeURIComponent(taskId)}`, { method: 'DELETE' });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Retrospectives (3)
  // ════════════════════════════════════════════════════════════════════════

  /** Upsert (lead). */
  async upsertRetrospective(eventId: string, data: UpsertEventRetrospectiveRequest): Promise<EventRetrospective> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/retrospective`, 'PUT', data);
  }

  async getRetrospective(eventId: string): Promise<EventRetrospective | null> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/retrospective`);
  }

  /** Admin. */
  async deleteRetrospective(eventId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/retrospective`, {
      method: 'DELETE',
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Comments (7) — JSON *or* multipart on the same path (guide §4)
  // ════════════════════════════════════════════════════════════════════════

  private commentBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/comments`
      : `/events/projects/${encodeURIComponent(targetId)}/comments`;
  }

  async getComments(
    targetType: 'event' | 'project',
    targetId: string,
    page?: number,
    limit?: number,
  ): Promise<EventComment[]> {
    const raw = await this.request<unknown>(
      `${this.commentBasePath(targetType, targetId)}${this.buildQueryString({ page, limit })}`,
    );
    return this.normalizePaginated<EventComment>(raw, page ?? 1, limit ?? 50).results;
  }

  /** JSON body `{ content }`. */
  async addComment(targetType: 'event' | 'project', targetId: string, content: string): Promise<EventComment> {
    return this.json(this.commentBasePath(targetType, targetId), 'POST', { content });
  }

  /** Multipart: `content` field + `file`/`files*` image parts. */
  async addCommentWithImages(
    targetType: 'event' | 'project',
    targetId: string,
    formData: FormData,
  ): Promise<EventComment> {
    const response = await fetch(
      `${EVENT_TEAM_API_BASE_URL}${this.commentBasePath(targetType, targetId)}`,
      { method: 'POST', body: formData },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new EventTeamApiError(response.status, response.statusText, errorText);
    }
    return response.json();
  }

  async getComment(commentId: string): Promise<EventComment> {
    return this.request(`/events/comments/${encodeURIComponent(commentId)}`);
  }

  async updateComment(commentId: string, content: string): Promise<EventComment> {
    return this.json(`/events/comments/${encodeURIComponent(commentId)}`, 'PATCH', { content });
  }

  /** Deletes attached images too (DB + R2). */
  async deleteComment(commentId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/comments/${encodeURIComponent(commentId)}`, { method: 'DELETE' });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Media (7)
  // ════════════════════════════════════════════════════════════════════════

  private imagesBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/images`
      : `/events/projects/${encodeURIComponent(targetId)}/images`;
  }

  async listImages(
    targetType: 'event' | 'project',
    targetId: string,
    page?: number,
    limit?: number,
  ): Promise<EventMedia[]> {
    const raw = await this.request<unknown>(
      `${this.imagesBasePath(targetType, targetId)}${this.buildQueryString({ page, limit })}`,
    );
    return this.normalizePaginated<EventMedia>(raw, page ?? 1, limit ?? 100).results;
  }

  /**
   * Multipart: single `file` part or repeated `files*` parts; optional `caption`
   * applies to all files. `image/*` only. Single file → one EventMedia; multiple → array.
   */
  async uploadImages(
    targetType: 'event' | 'project',
    targetId: string,
    formData: FormData,
  ): Promise<EventMedia | EventMedia[]> {
    const response = await fetch(
      `${EVENT_TEAM_API_BASE_URL}${this.imagesBasePath(targetType, targetId)}/upload`,
      { method: 'POST', body: formData },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new EventTeamApiError(response.status, response.statusText, errorText);
    }
    return response.json();
  }

  async updateImageCaption(mediaId: string, caption: string | null): Promise<EventMedia> {
    return this.json(`/events/images/${encodeURIComponent(mediaId)}`, 'PATCH', { caption });
  }

  /** Admin. */
  async deleteImage(mediaId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/images/${encodeURIComponent(mediaId)}`, { method: 'DELETE' });
  }

  /** Bytes with `Content-Disposition: inline` — fetch → blob URL for `<img src>`. */
  async downloadImage(mediaId: string): Promise<Response> {
    return this.requestRaw(`/events/images/${encodeURIComponent(mediaId)}/download`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Tags (10)
  // ════════════════════════════════════════════════════════════════════════

  /** KV-cached. */
  async listTags(): Promise<EventTag[]> {
    return this.request('/events/tags');
  }

  /** Admin; 409 on duplicate name. */
  async createTag(data: CreateEventTagRequest): Promise<EventTag> {
    return this.json('/events/tags', 'POST', data);
  }

  async getTag(id: string): Promise<EventTag> {
    return this.request(`/events/tags/${encodeURIComponent(id)}`);
  }

  async updateTag(id: string, data: UpdateEventTagRequest): Promise<EventTag> {
    return this.json(`/events/tags/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  async deleteTag(id: string): Promise<{ ok: boolean }> {
    return this.request(`/events/tags/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Reverse: events/projects carrying a tag. */
  async getTagTargets(id: string, targetType?: 'event' | 'project'): Promise<{ events: string[]; projects: string[] }> {
    return this.request(
      `/events/tags/${encodeURIComponent(id)}/targets${this.buildQueryString({ target_type: targetType })}`,
    );
  }

  private tagTargetPath(targetType: 'event' | 'project', targetId: string, tagId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/tags/${encodeURIComponent(tagId)}`
      : `/events/projects/${encodeURIComponent(targetId)}/tags/${encodeURIComponent(tagId)}`;
  }

  async addTagToTarget(targetType: 'event' | 'project', targetId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request(this.tagTargetPath(targetType, targetId, tagId), { method: 'POST' });
  }

  async removeTagFromTarget(targetType: 'event' | 'project', targetId: string, tagId: string): Promise<{ ok: boolean }> {
    return this.request(this.tagTargetPath(targetType, targetId, tagId), { method: 'DELETE' });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Web links (6)
  // ════════════════════════════════════════════════════════════════════════

  private webLinksBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/web-links`
      : `/events/projects/${encodeURIComponent(targetId)}/web-links`;
  }

  async listWebLinks(targetType: 'event' | 'project', targetId: string): Promise<EventWebLink[]> {
    return this.request(this.webLinksBasePath(targetType, targetId));
  }

  async createWebLink(
    targetType: 'event' | 'project',
    targetId: string,
    data: CreateEventWebLinkRequest,
  ): Promise<EventWebLink> {
    return this.json(this.webLinksBasePath(targetType, targetId), 'POST', data);
  }

  async updateWebLink(linkId: string, data: UpdateEventWebLinkRequest): Promise<EventWebLink> {
    return this.json(`/events/web-links/${encodeURIComponent(linkId)}`, 'PATCH', data);
  }

  async deleteWebLink(linkId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/web-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Clearance (19) — guide §3
  // ════════════════════════════════════════════════════════════════════════

  async listCheckTeams(params?: {
    event_id?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<EventPaginatedResponse<EventPlayerTeam>> {
    const raw = await this.request<unknown>(
      `/events/check-teams${this.buildQueryString(params || {})}`,
    );
    return this.normalizePaginated<EventPlayerTeam>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  /** Supports inline `members`. */
  async createCheckTeam(data: CreatePlayerTeamRequest): Promise<EventPlayerTeam> {
    return this.json('/events/check-teams', 'POST', data);
  }

  async getCheckTeam(id: string): Promise<EventPlayerTeamWithMembers> {
    return this.request(`/events/check-teams/${encodeURIComponent(id)}`);
  }

  async updateCheckTeam(id: string, data: UpdatePlayerTeamRequest): Promise<EventPlayerTeam> {
    return this.json(`/events/check-teams/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  /** Members + results cascade. */
  async deleteCheckTeam(id: string): Promise<{ ok: boolean }> {
    return this.request(`/events/check-teams/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listCheckTeamMembers(id: string): Promise<EventPlayerTeamMember[]> {
    return this.request(`/events/check-teams/${encodeURIComponent(id)}/members`);
  }

  async addCheckTeamMember(
    id: string,
    data: { user_uuid: string; display_name?: string | null },
  ): Promise<EventPlayerTeamMember> {
    return this.json(`/events/check-teams/${encodeURIComponent(id)}/members`, 'POST', data);
  }

  async removeCheckTeamMember(id: string, uuid: string): Promise<{ ok: boolean }> {
    return this.request(
      `/events/check-teams/${encodeURIComponent(id)}/members/${encodeURIComponent(uuid)}`,
      { method: 'DELETE' },
    );
  }

  /** One click to pull an event's participant list into a team. */
  async importParticipantsToTeam(teamId: string, eventId: string): Promise<{ imported: number }> {
    return this.request(
      `/events/check-teams/${encodeURIComponent(teamId)}/import-participants/${encodeURIComponent(eventId)}`,
      { method: 'POST' },
    );
  }

  async listChecks(params?: {
    event_id?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<EventPaginatedResponse<EventCommandCheck>> {
    const raw = await this.request<unknown>(`/events/checks${this.buildQueryString(params || {})}`);
    return this.normalizePaginated<EventCommandCheck>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  async createCheck(data: CreateCommandCheckRequest): Promise<EventCommandCheck> {
    return this.json('/events/checks', 'POST', data);
  }

  async getCheck(id: string): Promise<EventCommandCheck> {
    return this.request(`/events/checks/${encodeURIComponent(id)}`);
  }

  /** 400 when `time_end <= time_start`. */
  async updateCheck(id: string, data: UpdateCommandCheckRequest): Promise<EventCommandCheck> {
    return this.json(`/events/checks/${encodeURIComponent(id)}`, 'PATCH', data);
  }

  /** Results cascade. */
  async deleteCheck(id: string): Promise<{ ok: boolean }> {
    return this.request(`/events/checks/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  /** Ready-to-run ES query specs; defaults to all teams linked to the check's event. */
  async getCheckEsQuery(id: string, teamIds?: string[]): Promise<EsQuerySpec> {
    return this.request(
      `/events/checks/${encodeURIComponent(id)}/es-query${this.buildQueryString({
        team_ids: teamIds && teamIds.length > 0 ? teamIds.join(',') : undefined,
      })}`,
    );
  }

  /** Lead. Each submission is a new run; history preserved; check flips to `completed`. */
  async submitCheckResults(id: string, results: SubmitCheckTeamResult[], checkSignature?: string): Promise<SubmitCheckResultsResponse> {
    return this.json(`/events/checks/${encodeURIComponent(id)}/results`, 'POST', { results, check_signature: checkSignature });
  }

  async getEventRewardPrefix(eventId: string): Promise<EventRewardPrefix | null> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/reward-prefix`);
  }
  async setEventRewardPrefix(eventId: string, prefixId: number | null): Promise<EventRewardPrefix | null> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/reward-prefix`, 'PUT', { prefix_id: prefixId });
  }
  async previewCheckRewards(checkId: string): Promise<CheckRewardPreview> {
    return this.request(`/events/checks/${encodeURIComponent(checkId)}/rewards`);
  }
  /** `participants_added`: rewarded players newly added to the event's participants. */
  async grantCheckRewards(checkId: string, runId: string, prefixId: number): Promise<CheckRewardPreview & { granted: number; considered: number; participants_added?: number }> {
    return this.json(`/events/checks/${encodeURIComponent(checkId)}/rewards`, 'POST', { run_id: runId, prefix_id: prefixId });
  }

  /** All runs, or one run via `run_id`. */
  async getCheckResults(id: string, runId?: string): Promise<EventCheckResult[]> {
    return this.request(
      `/events/checks/${encodeURIComponent(id)}/results${this.buildQueryString({ run_id: runId })}`,
    );
  }

  /** "What teams are clear" — latest run; missing teams marked `unchecked`. */
  async getCheckSummary(id: string): Promise<EventCheckSummary> {
    return this.request(`/events/checks/${encodeURIComponent(id)}/summary`);
  }

  /** "What team member used what command". */
  async getResultViolations(resultId: string): Promise<EventCheckViolation[]> {
    return this.request(`/events/check-results/${encodeURIComponent(resultId)}/violations`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Calendar (10)
  // ════════════════════════════════════════════════════════════════════════

  private calendarBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/calendar`
      : `/events/projects/${encodeURIComponent(targetId)}/calendar`;
  }

  async listCalendarEvents(targetType: 'event' | 'project', targetId: string): Promise<CalendarEvent[]> {
    return this.request(this.calendarBasePath(targetType, targetId));
  }

  async listUpcomingCalendarEvents(targetType: 'event' | 'project', targetId: string): Promise<CalendarEvent[]> {
    return this.request(`${this.calendarBasePath(targetType, targetId)}/upcoming`);
  }

  async linkCalendarEvent(
    targetType: 'event' | 'project',
    targetId: string,
    calendarEventId: string,
  ): Promise<{ ok: boolean }> {
    return this.request(
      `${this.calendarBasePath(targetType, targetId)}/${encodeURIComponent(calendarEventId)}`,
      { method: 'POST' },
    );
  }

  async unlinkCalendarEvent(
    targetType: 'event' | 'project',
    targetId: string,
    calendarEventId: string,
  ): Promise<{ ok: boolean }> {
    return this.request(
      `${this.calendarBasePath(targetType, targetId)}/${encodeURIComponent(calendarEventId)}`,
      { method: 'DELETE' },
    );
  }

  /**
   * One-click scheduling (lead): creates + links a calendar event, sets
   * `scheduled_start/end` and advances idea/approved → `scheduled`.
   */
  async scheduleEvent(eventId: string, data: ScheduleEventRequest): Promise<ScheduleEventResponse> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/schedule`, 'POST', data);
  }

  /** Reverse lookup: events/projects linked to a calendar event. */
  async getTargetsForCalendarEvent(calendarEventId: string): Promise<EventCalendarReverseLink[]> {
    return this.request(`/events/calendar/event/${encodeURIComponent(calendarEventId)}`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Server links (8)
  // ════════════════════════════════════════════════════════════════════════

  private serversBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/servers`
      : `/events/projects/${encodeURIComponent(targetId)}/servers`;
  }

  async listServerLinks(targetType: 'event' | 'project', targetId: string): Promise<EventServerLink[]> {
    return this.request(this.serversBasePath(targetType, targetId));
  }

  /** One of `fqdn` / `cluster_id` required. */
  async createServerLink(
    targetType: 'event' | 'project',
    targetId: string,
    data: CreateEventServerLinkRequest,
  ): Promise<EventServerLink> {
    return this.json(this.serversBasePath(targetType, targetId), 'POST', data);
  }

  async updateServerLink(linkId: string, data: UpdateEventServerLinkRequest): Promise<EventServerLink> {
    return this.json(`/events/server-links/${encodeURIComponent(linkId)}`, 'PATCH', data);
  }

  async deleteServerLink(linkId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/server-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
  }

  /** Find events/projects by server FQDN. */
  async getServerLinksByFqdn(fqdn: string): Promise<EventServerLink[]> {
    return this.request(`/events/servers/fqdn/${encodeURIComponent(fqdn)}`);
  }

  /** Find events/projects by cluster. */
  async getServerLinksByCluster(clusterId: string): Promise<EventServerLink[]> {
    return this.request(`/events/servers/cluster/${encodeURIComponent(clusterId)}`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Modpack links (7)
  // ════════════════════════════════════════════════════════════════════════

  private modpacksBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/modpacks`
      : `/events/projects/${encodeURIComponent(targetId)}/modpacks`;
  }

  async listModpackLinks(targetType: 'event' | 'project', targetId: string): Promise<EventModpackLink[]> {
    return this.request(this.modpacksBasePath(targetType, targetId));
  }

  async createModpackLink(
    targetType: 'event' | 'project',
    targetId: string,
    data: CreateEventModpackLinkRequest,
  ): Promise<EventModpackLink> {
    return this.json(this.modpacksBasePath(targetType, targetId), 'POST', data);
  }

  async updateModpackLink(linkId: string, data: UpdateEventModpackLinkRequest): Promise<EventModpackLink> {
    return this.json(`/events/modpack-links/${encodeURIComponent(linkId)}`, 'PATCH', data);
  }

  async deleteModpackLink(linkId: string): Promise<{ ok: boolean }> {
    return this.request(`/events/modpack-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
  }

  /** Find events/projects by modpack name. */
  async getModpackLinksByName(name: string): Promise<EventModpackLink[]> {
    return this.request(`/events/modpacks/name/${encodeURIComponent(name)}`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Ticket links (7)
  // ════════════════════════════════════════════════════════════════════════

  private ticketsBasePath(targetType: 'event' | 'project', targetId: string): string {
    return targetType === 'event'
      ? `/events/items/${encodeURIComponent(targetId)}/tickets`
      : `/events/projects/${encodeURIComponent(targetId)}/tickets`;
  }

  async listTicketLinks(targetType: 'event' | 'project', targetId: string): Promise<EventTicketLink[]> {
    return this.request(this.ticketsBasePath(targetType, targetId));
  }

  async linkTicket(targetType: 'event' | 'project', targetId: string, ticketId: number): Promise<{ ok: boolean }> {
    return this.json(this.ticketsBasePath(targetType, targetId), 'POST', { ticket_id: ticketId });
  }

  async unlinkTicket(targetType: 'event' | 'project', targetId: string, ticketId: number): Promise<{ ok: boolean }> {
    return this.request(`${this.ticketsBasePath(targetType, targetId)}/${ticketId}`, {
      method: 'DELETE',
    });
  }

  /** Reverse lookup: events/projects linked to a ticket. */
  async getTargetsForTicket(ticketId: number): Promise<EventTicketLink[]> {
    return this.request(`/events/tickets/${ticketId}`);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Entity links (6)
  // ════════════════════════════════════════════════════════════════════════

  private entityLinksBasePath(sourceType: 'event' | 'project', sourceId: string): string {
    return sourceType === 'event'
      ? `/events/items/${encodeURIComponent(sourceId)}/links`
      : `/events/projects/${encodeURIComponent(sourceId)}/links`;
  }

  async getEntityLinks(sourceType: 'event' | 'project', sourceId: string): Promise<EventEntityLinksResponse> {
    return this.request(this.entityLinksBasePath(sourceType, sourceId));
  }

  async createEntityLink(
    sourceType: 'event' | 'project',
    sourceId: string,
    data: CreateEventEntityLinkRequest,
  ): Promise<{ ok: boolean }> {
    return this.json(this.entityLinksBasePath(sourceType, sourceId), 'POST', data);
  }

  async deleteEntityLink(
    sourceType: 'event' | 'project',
    sourceId: string,
    targetType: 'event' | 'project',
    targetId: string,
  ): Promise<{ ok: boolean }> {
    return this.request(
      `${this.entityLinksBasePath(sourceType, sourceId)}/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`,
      { method: 'DELETE' },
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Activity (2)
  // ════════════════════════════════════════════════════════════════════════

  async getRecentActivity(limit?: number): Promise<EventActivityLog[]> {
    return this.request(`/events/activity/recent${this.buildQueryString({ limit })}`);
  }

  async getActivityByActor(actorId: string, page?: number, limit?: number): Promise<EventActivityLog[]> {
    return this.request(
      `/events/activity/by/${encodeURIComponent(actorId)}${this.buildQueryString({ page, limit })}`,
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // Kanban workspaces (15) — guide §1.5
  // ════════════════════════════════════════════════════════════════════════

  /**
   * TOP-LEVEL path (guide §7.3): `GET /events-with-kanban-summary` — do NOT
   * prefix with `/events/`.
   */
  async getEventsWithKanbanSummary(params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<EventPaginatedResponse<EventWithKanbanSummary>> {
    const raw = await this.request<unknown>(
      `/events-with-kanban-summary${this.buildQueryString(params || {})}`,
    );
    return this.normalizePaginated<EventWithKanbanSummary>(raw, params?.page ?? 1, params?.limit ?? 50);
  }

  /** Denormalised link + board + card summaries. */
  async getEventWorkspaces(eventId: string): Promise<EventWorkspaceSummary[]> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/workspaces`);
  }

  /** Raw bridge links. */
  async listEventKanbanLinks(eventId: string): Promise<EventKanbanLink[]> {
    return this.request(`/events/items/${encodeURIComponent(eventId)}/kanban-links`);
  }

  /** Link an existing card (lead + kanban write). */
  async createEventKanbanLink(eventId: string, data: CreateEventKanbanLinkRequest): Promise<EventKanbanLink> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/kanban-links`, 'POST', data);
  }

  async updateEventKanbanLink(
    eventId: string,
    cardId: string,
    data: UpdateEventKanbanLinkRequest,
  ): Promise<EventKanbanLink> {
    return this.json(
      `/events/items/${encodeURIComponent(eventId)}/kanban-links/${encodeURIComponent(cardId)}`,
      'PATCH',
      data,
    );
  }

  async deleteEventKanbanLink(eventId: string, cardId: string): Promise<{ ok: boolean }> {
    return this.request(
      `/events/items/${encodeURIComponent(eventId)}/kanban-links/${encodeURIComponent(cardId)}`,
      { method: 'DELETE' },
    );
  }

  /**
   * Promote to card — idempotent per (event, board): 201 `created_card:true`
   * or 200 `created_card:false` (treat both as success — guide §7.2).
   */
  async promoteToCard(eventId: string, data: PromoteEventToCardRequest): Promise<PromoteEventToCardResponse> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/promote-to-card`, 'POST', data);
  }

  /** Merged event + linked-card timeline, newest first. */
  async getEventTimeline(
    eventId: string,
    params?: { since?: number; limit?: number },
  ): Promise<EventTimelineEntry[]> {
    return this.request(
      `/events/items/${encodeURIComponent(eventId)}/timeline${this.buildQueryString(params || {})}`,
    );
  }

  // ── With-sync companions (use when ≥1 link has a sync flag on — guide §1.5) ──

  /** Propagates status → mapped list, priority, due_date to linked cards. */
  async updateEventWithSync(eventId: string, data: UpdateEventItemRequest): Promise<EventItem> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/with-sync`, 'PATCH', data);
  }

  /** JSON only. Mirrored to linked cards when `sync_comments`. */
  async addCommentWithSync(eventId: string, content: string): Promise<EventComment> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/comments/with-sync`, 'POST', {
      content,
    });
  }

  /** Assign a roster member by UUID AND mirror to linked cards. */
  async addAssigneeWithSync(
    eventId: string,
    userUuid: string,
    role?: EventAssignmentRole,
  ): Promise<EventAssignment> {
    return this.json(`/events/items/${encodeURIComponent(eventId)}/assignees/with-sync`, 'POST', {
      user_uuid: userUuid,
      role,
    });
  }

  async removeAssigneeWithSync(eventId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request(
      `/events/items/${encodeURIComponent(eventId)}/assignees/${encodeURIComponent(userUuid)}/with-sync`,
      { method: 'DELETE' },
    );
  }

  // ── Card/board reverse side ──────────────────────────────────────────────

  /** Events linked to a card. */
  async getEventsForCard(cardId: string): Promise<EventItem[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/events`);
  }

  /** Events linked to any card on a board (lead + kanban). */
  async getEventsForBoard(boardId: string): Promise<EventItem[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/events`);
  }

  /** Create an event from a card and bridge them — idempotent (200 `created_event:false`). */
  async adoptCardAsEvent(cardId: string, data: AdoptCardAsEventRequest): Promise<AdoptCardAsEventResponse> {
    return this.json(`/kanban/cards/${encodeURIComponent(cardId)}/adopt-as-event`, 'POST', data);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Event Quest SNBT (docs/EVENT_QUEST_SNBT_DOCS.md) — per event, not project.
  // Visual editor *save* is intentionally not wired — viewer is read-only.
  // ════════════════════════════════════════════════════════════════════════

  private questsBase(eventId: string): string {
    return `/events/items/${encodeURIComponent(eventId)}/quests`;
  }

  async listEventQuests(eventId: string): Promise<EventQuestFilePublic[]> {
    const raw = await this.request<unknown>(this.questsBase(eventId));
    return Array.isArray(raw) ? (raw as EventQuestFilePublic[]) : [];
  }

  async getEventQuestCount(eventId: string): Promise<EventQuestCount> {
    return this.request(`${this.questsBase(eventId)}/count`);
  }

  async getEventQuestManifest(eventId: string): Promise<EventQuestManifest> {
    return this.request(`${this.questsBase(eventId)}/manifest`);
  }

  async getEventChapterGroups(eventId: string): Promise<EventQuestChapterGroupsResponse> {
    return this.request(`${this.questsBase(eventId)}/chapter-groups`);
  }

  async getEventQuestBundle(eventId: string): Promise<EventQuestBundle> {
    return this.request(`${this.questsBase(eventId)}/bundle`);
  }

  /**
   * Multipart upsert (`file` / `files*` / `content`+`filename`). Same canonical
   * filename replaces the previous bytes (`created: false`).
   */
  async uploadEventQuests(
    eventId: string,
    formData: FormData,
  ): Promise<EventQuestUpsertResult | EventQuestUpsertResult[]> {
    const response = await fetch(`${EVENT_TEAM_API_BASE_URL}${this.questsBase(eventId)}/upload`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new EventTeamApiError(response.status, response.statusText, errorText);
    }
    return response.json();
  }

  async upsertEventQuest(
    eventId: string,
    data: EventQuestJsonUpsertRequest,
  ): Promise<EventQuestUpsertResult> {
    return this.json(`${this.questsBase(eventId)}`, 'POST', data);
  }

  async getQuestFile(eventId: string, filename: string): Promise<EventQuestFilePublic> {
    return this.request(`${this.questsBase(eventId)}/files/${encodeURIComponent(filename)}`);
  }

  async downloadQuestFile(eventId: string, filename: string): Promise<Response> {
    return this.requestRaw(`${this.questsBase(eventId)}/files/${encodeURIComponent(filename)}/download`);
  }

  async serveQuestFile(eventId: string, filename: string): Promise<Response> {
    return this.requestRaw(`${this.questsBase(eventId)}/files/${encodeURIComponent(filename)}/serve`);
  }

  /** Viewer load: `{ file, content, parsed, editor }`. */
  async getQuestEditor(eventId: string, filename: string): Promise<EventQuestEditorDocument> {
    return this.request(`${this.questsBase(eventId)}/files/${encodeURIComponent(filename)}/editor`);
  }

  /** Raw SNBT replace of an already-uploaded file. */
  async replaceQuestFile(
    eventId: string,
    filename: string,
    data: EventQuestJsonUpsertRequest,
  ): Promise<EventQuestUpsertResult> {
    return this.json(`${this.questsBase(eventId)}/files/${encodeURIComponent(filename)}`, 'PUT', data);
  }

  /** Admin. Blocked for `chapter_groups.snbt` while chapter files remain. */
  async deleteQuestFile(eventId: string, filename: string): Promise<{ ok: boolean }> {
    return this.request(`${this.questsBase(eventId)}/files/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
  }
}

export const eventTeamApiService = new EventTeamApiService();
